import get from 'lodash/get';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useHistory } from 'react-router-dom';
import qs from 'query-string';
import {
	initAction,
	changeAction,
	getEncarpayApplicationsByIdAction,
	postEncarpayApplicationsAction,
	postEncarpayClientsAction,
	getEncarpayVehiclesHyundaiLimitAction,
	postEncarpayVehiclesHyundaiLimitAction,
	postEncarpayAgreementsAction,
	putEncarpayClientAddressAction,
	getEncarPayJBCreditLimitAction,
	getEncarPayBNKCreditLimitAction,
	postEncarpayReapplyCreditLimitAction,
	getEncarpayOneStopLimitAction,
} from 'app/store/modules/encarpay/apply';
import { ENCARPAY_INFLOW } from 'app/constants/encarpay';
import { createLocationSearch } from 'app/libs/urlParse';
import { useUniversalCloser } from 'app/hook/common/useUniversalCloser';
import { HOMESERVICE_PAYMENT_METHODS } from 'app/constants/homeservice';

const carHost = process.env.REACT_APP_ENCAR_FEM_CAR;

export default function useApplyEncarpay() {
	const { apply } = useSelector((state) => state.encarpay, shallowEqual);
	const dispatch = useDispatch();
	const history = useHistory();
	const { handleCheckPrevPageClose, handleUniversalClose } = useUniversalCloser({
		browser: {
			onNotClosed: () => window.history.back(),
		},
	});

	const { interestInfo, mainInterestRate, carId, carMaximumLimit, showTerms, error, inflow, redirectURL, backURL, judgementStatus, hash } = apply;

	const encarpayInflow = useMemo(
		() => ({
			isHomeService: inflow === ENCARPAY_INFLOW.MOBILE_ENCAR_PAY_HOME_SERVICE || inflow === ENCARPAY_INFLOW.MOBILE_INTEREST_FREE_HOME_SERVICE, // 홈서비스 엔카페이 · 무이자 할부
			isHomeServiceLoan: inflow === ENCARPAY_INFLOW.MOBILE_ENCAR_PAY_HOME_SERVICE_LOAN, // 홈서비스 엔카페이 일반대출
			isHomeServiceSafety: inflow === ENCARPAY_INFLOW.MOBILE_ENCAR_PAY_HOME_SERVICE_SAFETY,
			isAffiliationDealer: inflow === ENCARPAY_INFLOW.MOBILE_ENCAR_PAY_AFFILIATE_DEALER,
			isMobileEncarPay: inflow === ENCARPAY_INFLOW.MOBILE_ENCAR_PAY || inflow === ENCARPAY_INFLOW.MOBILE_ENCAR_PAY_NORMAL,
			isMeetgo: inflow === ENCARPAY_INFLOW.MOBILE_ENCAR_PAY_MEET_GO,
			isFastTrack: inflow === ENCARPAY_INFLOW.MOBILE_ENCAR_PAY_FAST,
		}),
		[inflow],
	);

	const initEncarpayApplyState = useCallback(() => {
		dispatch(initAction());
	}, [dispatch]);

	const getEncarpayApplyState = useCallback((path) => (path ? get(apply, path) : apply), [apply]);

	const setEncarpayApplyState = useCallback(
		(path, value) => {
			dispatch(
				changeAction({
					path,
					value,
				}),
			);
		},
		[dispatch],
	);

	const cancelEncarpayApplication = useCallback(
		(goToPrev = false) => {
			const search = qs.parse(history.location.search);
			const { isHsReapply, isCustomerLeadProcess } = search;
			const commSearchValue = createLocationSearch(
				history.location.search,
				`WT.hit=${search['WT.hit'] || ''}&login=${search.login || ''}&ew=${search.ew || ''}&extendedWarrantyMonth=${search.extendedWarrantyMonth || ''}`,
			);

			if (encarpayInflow.isHomeService) {
				if (isHsReapply === 'Y') {
					const hsBackUrl = `/home-service/reapply/${carId}${commSearchValue}`;
					history.replace(hsBackUrl);
					return;
				}
				if (isCustomerLeadProcess === 'Y') {
					const hsBackUrl = `/encar-pay/reapply-intro${commSearchValue}`;
					history.replace(hsBackUrl);
					return;
				}

				window.location.href = carHost;
				return;
			}

			if (encarpayInflow.isHomeServiceSafety) {
				history.replace(`/escrow/application/cars/${carId}/estimate${commSearchValue}`);
				return;
			}

			if (backURL) {
				history.replace(backURL);
				return;
			}

			if (
				goToPrev ||
				search.inflow === ENCARPAY_INFLOW.MOBILE_ENCAR_PAY_HOME_SERVICE ||
				search.inflow === ENCARPAY_INFLOW.MOBILE_INTEREST_FREE_HOME_SERVICE
			) {
				handleCheckPrevPageClose();
			} else {
				handleUniversalClose();
			}
		},
		[backURL, carId, encarpayInflow.isHomeService, encarpayInflow.isHomeServiceSafety, handleCheckPrevPageClose, handleUniversalClose, history],
	);

	const postEncarpayApplications = useCallback((data) => dispatch(postEncarpayApplicationsAction(data)), [dispatch]);
	const getEncarpayApplicationsById = useCallback((applicationId) => dispatch(getEncarpayApplicationsByIdAction(applicationId)), [dispatch]);
	const postEncarpayClients = useCallback((judgementId, data) => dispatch(postEncarpayClientsAction(judgementId, data)), [dispatch]);
	const postEncarpayAgreements = useCallback((judgementId, data) => dispatch(postEncarpayAgreementsAction(judgementId, data)), [dispatch]);
	const getEncarpayVehiclesHyundaiLimit = useCallback(
		(judgementId, axiosConfig) => dispatch(getEncarpayVehiclesHyundaiLimitAction(judgementId, axiosConfig)),
		[dispatch],
	);
	const postEncarpayVehiclesHyundaiLimit = useCallback(
		(judgementId, axiosConfig) => dispatch(postEncarpayVehiclesHyundaiLimitAction(judgementId, axiosConfig)),
		[dispatch],
	);

	const getEncarpayVehiclesOneStopLimit = useCallback(
		(judgementId, axiosConfig) => dispatch(getEncarpayOneStopLimitAction(judgementId, axiosConfig)),
		[dispatch],
	);

	const getEncarpayVehiclesJBLimit = useCallback(
		(judgementId, axiosConfig) => dispatch(getEncarPayJBCreditLimitAction(judgementId, axiosConfig)),
		[dispatch],
	);
	const getEncarpayVehiclesBNKLimit = useCallback(
		(judgementId, axiosConfig) => dispatch(getEncarPayBNKCreditLimitAction(judgementId, axiosConfig)),
		[dispatch],
	);
	const putEncarpayClientAddress = useCallback((clientId, data) => dispatch(putEncarpayClientAddressAction(clientId, data)), [dispatch]);

	const reservationId = backURL?.match(/reservation\/(.*?)\/options/)?.[1] || null;
	const postEncarpayReapplyCreditLimit = useCallback(
		(_inflow, data, axiosConfig) => dispatch(postEncarpayReapplyCreditLimitAction(_inflow, data, axiosConfig)),
		[dispatch],
	);
	const isFreeInterestEncarPay = useCallback(
		(paymentMethod, loanVersion) => paymentMethod === HOMESERVICE_PAYMENT_METHODS.ENCAR_PAY && loanVersion === 'V2',
		[],
	);

	return {
		carId,
		reservationId,
		encarpayInflow,
		applicationId: apply.applicationId,
		carMaximumLimit,
		showTerms,
		interestInfo,
		error,
		redirectURL,
		backURL,
		judgementStatus,
		hash,
		initEncarpayApplyState,
		getEncarpayApplyState,
		setEncarpayApplyState,
		postEncarpayApplications,
		getEncarpayApplicationsById,
		postEncarpayClients,
		postEncarpayAgreements,
		getEncarpayVehiclesHyundaiLimit,
		postEncarpayVehiclesHyundaiLimit,
		getEncarpayVehiclesOneStopLimit,
		getEncarpayVehiclesJBLimit,
		getEncarpayVehiclesBNKLimit,
		putEncarpayClientAddress,
		cancelEncarpayApplication,
		mainInterestRate,
		postEncarpayReapplyCreditLimit,
		isFreeInterestEncarPay,
	};
}
